// TODO: sửa lại chỉ dùng 1 biến màu và lắng nghe nó thay đổi để lấy mấy cái thông tin cần thiết khác
'use client'

import {
  useAddToCart,
  useCartMiniQuery,
} from '@/hooks/react-query/cart/use-cart'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { safeTranslateColor } from '@/lib/helpers/sanitize-i18n-keys'
import { trackPixelEvent } from '@/lib/tracking/track-pixel'
import { stringToSlug } from '@/lib/utils'
import {
  calculateCampaignPrice,
  extractCampaignSnapshot,
  getDisplayPriceFromCampaignResult,
  isProductExcludedByActiveCampaign,
} from '@/lib/utils/campaign-price'
import { useIsBuy1Get1ValueMatchCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { CAMPAIGN_NAMES } from '@/lib/utils/campaign/campaign-names'
import { LocalStorage } from '@/lib/utils/local-storage'
import {
  useAllCampaigns,
  useCampaign,
  useCampaignActive,
} from '@/providers/campaign-provider'
import { Option, ProductOptionsValue, VariantMapped } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { useTranslations } from 'next-intl'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export interface ProductDetailContextType {
  product: ProductDetailFormatted | null
  quantity: number
  sizeSelected: string | null
  colorSelected: string | null
  variantActive: VariantMapped | null
  colorOptionActive: Option | null
  sizeOptions: string[]
  sortedColorOptions: Option[]
  setColorSelected: (color: string) => void
  setSizeSelected: (size: string) => void
  setColorOptionActive: (colorOption: Option) => void
  setVariantActive: (variant: VariantMapped) => void
  setProduct: (product: ProductDetailFormatted) => void
  setQuantity: (quantity: number) => void
  handleAddToCart: (payload: AddToCartPayload) => void
  isPendingAddToCart: boolean
  comboSelectedInfo: Record<string, string>
  setComboSelectedInfo: (info: Record<string, string>) => void
  scrollToFirstImageRef: React.MutableRefObject<(() => void) | null>
  activeGalleryIndex: number
  setActiveGalleryIndex: (index: number) => void
  scrollToGalleryIndexRef: React.MutableRefObject<
    ((index: number) => void) | null
  >
}

const ProductDetailContext = createContext<ProductDetailContextType>({
  product: null,
  quantity: 1,
  sizeSelected: null,
  colorSelected: null,
  variantActive: null,
  colorOptionActive: null,
  sizeOptions: [],
  sortedColorOptions: [],
  setColorSelected: () => {},
  setSizeSelected: () => {},
  setColorOptionActive: () => {},
  setVariantActive: () => {},
  setProduct: () => {},
  handleAddToCart: () => {},
  setQuantity: () => {},
  isPendingAddToCart: false,
  comboSelectedInfo: {},
  setComboSelectedInfo: () => {},
  scrollToFirstImageRef: { current: null },
  activeGalleryIndex: 0,
  setActiveGalleryIndex: () => {},
  scrollToGalleryIndexRef: { current: null },
})

export const useProductDetail = () => {
  const context = useContext(ProductDetailContext)
  if (!context) {
    throw new Error(
      'useProductDetail must be used within ProductDetailProvider'
    )
  }
  return context
}

// Thêm hook bao bọc để đảm bảo product không null
export const useProductDetailSafe = () => {
  const { product, ...rest } = useProductDetail()

  if (!product) {
    throw new Error('Product is not available')
  }

  return { product, ...rest }
}

interface ProductDetailProviderProps {
  children: React.ReactNode
  initialProduct: ProductDetailFormatted
}

interface AddToCartPayload {
  regularPrice: number
  comparePrice: number
  giftId?: string
  giftOptionId?: string
}

export const ProductDetailProvider = ({
  children,
  initialProduct,
}: ProductDetailProviderProps) => {
  const { mutateAsync: addToCart, isPending: isPendingAddToCart } =
    useAddToCart()
  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  const tColorTitle = useTranslations()

  // Lấy dữ liệu cần thiết để tính campaign price
  const { data: cartMini } = useCartMiniQuery({ enabled: true })
  const { campaigns } = useAllCampaigns()
  const campaignData = useCampaign(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  const isCampaignActive = useCampaignActive(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  const isBuy1Get1ValueMatchActive = useIsBuy1Get1ValueMatchCampaignActive()

  const [product, setProduct] = useState<ProductDetailFormatted>(initialProduct)
  const [variantActive, setVariantActive] = useState<VariantMapped | null>(null)
  const [colorOptionActive, setColorOptionActive] = useState<Option | null>(
    null
  )
  const [colorSelected, setColorSelected] = useState<string>('')
  const [sizeSelected, setSizeSelected] = useState<string>('')
  const [quantity, setQuantity] = useState<number>(1)
  const scrollToFirstImageRef = useRef<(() => void) | null>(null)
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0)
  const scrollToGalleryIndexRef = useRef<((index: number) => void) | null>(
    null
  )

  const [comboSelectedInfo, setComboSelectedInfo] = useState<
    Record<string, string>
  >(() => {
    const initialInfo: Record<string, string> = {}
    initialProduct?.combo?.options?.forEach((option, index) => {
      initialInfo[`option${index + 1}`] = option.values?.[0]?.value || ''
    })
    return initialInfo
  })

  // Sắp xếp kích thước theo thứ tự XS, S, M, L, XL, ...
  const sizeOptions = useMemo(() => {
    const allVariants = Object.values(product.mapped_variants || {}).flat()

    const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL']
    const sizeOrderMap = new Map(sizeOrder.map((size, index) => [size, index]))

    const sizeSet = new Set<string>()

    for (const variant of allVariants) {
      if (variant.size) sizeSet.add(variant.size)
    }

    return Array.from(sizeSet).sort((a, b) => {
      const indexA = sizeOrderMap.get(a) ?? Infinity
      const indexB = sizeOrderMap.get(b) ?? Infinity
      return indexA - indexB
    })
  }, [product.mapped_variants])

  const optionsValue = product?.options_value
  // Sắp xếp màu sắc theo thứ tự từ options
  const sortedColorOptions = useMemo(() => {
    const colorOptions =
      optionsValue?.find((option: ProductOptionsValue) =>
        option?.options_id?.includes('color')
      )?.options || []

    // Giữ nguyên thứ tự từ API
    return colorOptions
  }, [optionsValue])

  // Tự động chọn size khi chưa có size nào được chọn.
  // Đã chọn size rồi thì đổi màu vẫn giữ nguyên size đó (kể cả khi màu mới hết size)
  useEffect(() => {
    if (colorSelected && product.mapped_variants) {
      const colorKey = Object.keys(product.mapped_variants).find(
        (key) => stringToSlug(key) === colorSelected
      )

      if (colorKey && product.mapped_variants[colorKey].length > 0) {
        const availableVariants = product.mapped_variants[colorKey].filter(
          (variant: VariantMapped) => !variant.isDisable
        )

        // Ưu tiên size cuối cùng đã lưu từ bộ lọc size ở trang collection, nếu còn hàng
        const savedSizes = LocalStorage.get<string[]>('COLLECTION_FILTER_SIZE')
        const preferredSize = savedSizes?.[savedSizes.length - 1]
        const preferredVariant = preferredSize
          ? availableVariants.find(
              (variant) => variant.size?.toLowerCase() === preferredSize
            )
          : undefined

        const availableVariant = preferredVariant || availableVariants[0]

        if (availableVariant && availableVariant.size) {
          const fallbackSize = availableVariant.size
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setSizeSelected((currentSize) => currentSize || fallbackSize)
        }
      }
    }
  }, [colorSelected, product.mapped_variants])

  const comboAddToCart = (payload: AddToCartPayload) => {
    const { giftId, giftOptionId } = payload

    const comboSelected = product?.variants?.find((item) => {
      return Object.entries(comboSelectedInfo).every(([key, value]) => {
        return item[key as keyof typeof item] === value
      })
    })

    if (product?.is_setdo && !comboSelected) {
      console.warn('Không tìm thấy variant phù hợp', {
        comboSelectedInfo,
      })
      return
    }

    addToCartSync({
      product_id: product?.id || '',
      quantity,
      options: {
        id: comboSelected?.id || '',
      },
      prize_when_buy_with_collection: {
        product_id: giftId || '',
        options: {
          id: giftOptionId || '',
        },
      },
      product_name: product.title,
      variant_name: Object.values(comboSelectedInfo)
        .filter(Boolean)
        .join(' / '),
      regular_price: product.regular_price,
      compare_price: product.compare_price || 0,
      image_url: product.default_thumbnail || '',
    })
  }

  const handleAddToCart = (payload: AddToCartPayload) => {
    if (product?.is_setdo) {
      comboAddToCart(payload)
    } else {
      handleAddToCartNormal(payload)
    }
  }

  const handleAddToCartNormal = (payload: AddToCartPayload) => {
    const { giftId, giftOptionId } = payload

    const mappedVariants = product?.mapped_variants
    if (!mappedVariants) {
      console.warn('No mapped variants found')
      return
    }

    const colorKey = Object.keys(mappedVariants).find(
      (key) => stringToSlug(key) === colorSelected
    )

    if (!colorKey) {
      console.warn('Color không tồn tại trong mapped_variants', {
        colorSelected,
        availableColors: Object.keys(mappedVariants).map((key) =>
          stringToSlug(key)
        ),
      })
      return
    }

    const selectedVariant = mappedVariants[colorKey].find(
      (item) => item.size === sizeSelected
    )

    if (!selectedVariant) {
      console.warn('Không tìm thấy variant phù hợp', {
        colorKey,
        sizeSelected,
        availableSizes: mappedVariants[colorKey].map((v) => v.size),
      })
      return
    }

    // Size đang chọn đã hết hàng ở màu này (đổi màu vẫn giữ size)
    if (selectedVariant.isDisable) return

    // TODO: Add to cart logic here
    addToCartSync({
      product_id: selectedVariant.product_id,
      quantity,
      options: {
        id: selectedVariant?.id,
      },
      prize_when_buy_with_collection: {
        product_id: giftId || '',
        options: {
          id: giftOptionId || '',
        },
      },
      product_name: product.title,
      variant_name: `${safeTranslateColor(tColorTitle, colorOptionActive?.title || '')} / ${selectedVariant?.size || ''}`,
      regular_price: payload.regularPrice || product.regular_price,
      compare_price: payload.comparePrice || product.compare_price || 0,
      image_url: selectedVariant.thumbnail || product.default_thumbnail || '',
    })
  }

  const addToCartSync = async ({
    product_id,
    quantity,
    options,
    prize_when_buy_with_collection,
    product_name,
    variant_name,
    regular_price,
    compare_price,
    image_url,
  }: {
    product_id: string
    quantity: number
    options: {
      id: string
    }
    prize_when_buy_with_collection: {
      product_id: string
      options: {
        id: string
      }
    }
    product_name: string
    variant_name: string
    regular_price: number
    compare_price: number
    image_url: string
  }) => {
    // Snapshot campaign data tại thời điểm add to cart (trước khi invalidate)
    const campaignSnapshot = extractCampaignSnapshot({
      cartMini,
      campaignData,
      isCampaignActive,
      isBuy1Get1ValueMatchActive,
    })

    // Tính campaign price với snapshot data
    const campaignPriceResult = calculateCampaignPrice({
      regularPrice: regular_price,
      comparePrice: compare_price,
      ...campaignSnapshot,
      isExcluded: isProductExcludedByActiveCampaign({
        product,
        campaigns,
        regularPrice: regular_price,
        maxEligiblePriceForGift: cartMini?.max_eligible_price_for_gift,
      }),
    })

    // Tính display price theo cùng logic với product detail price
    const { displayPrice, displayComparePrice } =
      getDisplayPriceFromCampaignResult({
        campaignResult: campaignPriceResult,
        regularPrice: regular_price,
        comparePrice: compare_price,
      })

    await addToCart({
      payload: {
        product_id,
        quantity,
        options: {
          id: options.id,
        },
        prize_when_buy_with_collection,
      },
      toastOptions: {
        productId: product_id,
        productName: product_name,
        variantName: variant_name,
        regularPrice: displayPrice,
        comparePrice: displayComparePrice,
        imageUrl: image_url,
      },
    })

    trackEvent({
      event: ETrackingEvent.AddToCart,
      ecommerce: {
        currency: 'VND',
        value: product.regular_price,
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            price: product.regular_price,
            quantity: 1,
            item_brand: 'Coolmate',
            href: window?.location?.href,
            item_url: window?.location?.href,
            item_image: image_url,
            discount: product.sale_percent,
            item_variant: variant_name,
            item_rating: product?.review?.ratingValue || 0,
          },
        ],
      },
    })

    const ids = product.id + colorSelected

    trackPixelEvent('AddToCart', {
      content_name: product.title.replace('/\n/', '').trim(),
      content_category: product?.category,
      content_ids: [ids],
      content_type: 'product',
      value: Number(product.regular_price),
      currency: 'VND',
    })

    trackPixelEvent('InitiateCheckout', {
      content_ids: [ids],
      eventref: '',
    })
  }

  return (
    <ProductDetailContext.Provider
      value={{
        product,
        quantity,
        setQuantity,
        variantActive,
        colorSelected,
        sizeSelected,
        colorOptionActive,
        sizeOptions,
        sortedColorOptions,
        setVariantActive,
        setProduct,
        setColorOptionActive,
        setColorSelected,
        setSizeSelected,
        handleAddToCart,
        isPendingAddToCart,
        comboSelectedInfo,
        setComboSelectedInfo,
        scrollToFirstImageRef,
        activeGalleryIndex,
        setActiveGalleryIndex,
        scrollToGalleryIndexRef,
      }}
    >
      {children}
    </ProductDetailContext.Provider>
  )
}
